import { cn, Icon, MenuList, MenuListItem, ResponsivePopover, toast } from '@nexus/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { SwitchCompanyResult } from '../api/types';
import { useResetSession, useSession } from '../session/session';
import { CompanyMark } from './company-mark';

/**
 * Logo and name of the active company; opens the switch when the account may use others. In the
 * top bar from the tablet on, at the head of the menu on the phone (`inMenu`, full name).
 */
export function CompanySwitcher({
  inMenu = false,
  onSwitched,
}: {
  inMenu?: boolean;
  /** After the switch, e.g. to close the menu around it. */
  onSwitched?: () => void;
}) {
  const { t } = useTranslation();
  const session = useSession();
  const resetSession = useResetSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const company = session.company;
  if (!company) return null;

  const identity = (
    <>
      <CompanyMark company={company} />
      <span className={cn('min-w-0 truncate font-semibold text-fg', !inMenu && 'hidden md:block')}>
        {inMenu ? company.name : (company.shortName ?? company.name)}
      </span>
    </>
  );

  if (session.companies.length <= 1) {
    return <div className="flex min-w-0 items-center gap-2.5 px-1">{identity}</div>;
  }

  async function switchTo(companyId: string) {
    setPending(companyId);
    try {
      const result = await api.post<SwitchCompanyResult>('/auth/switch-company', { companyId });
      if (result.mode === 'login') {
        window.location.assign(result.url);
        return;
      }
      setOpen(false);
      await resetSession();
      await navigate({ to: '/' });
      onSwitched?.();
    } catch {
      toast.error(t('shell.company.switchFailed'));
    } finally {
      setPending(null);
    }
  }

  return (
    <ResponsivePopover
      open={open}
      onOpenChange={setOpen}
      align="start"
      title={t('shell.company.title')}
      trigger={
        <button
          type="button"
          aria-label={`${t('shell.company.switch')}: ${company.name}`}
          className={cn(
            'flex h-control min-w-0 cursor-pointer items-center gap-2.5 rounded-md px-1.5 text-left transition-colors transition-fast hover:bg-hover md:pr-2.5',
            inMenu && 'w-full pr-2.5',
          )}
        >
          {identity}
          <Icon
            name="chevrons-up-down"
            className={cn('shrink-0 text-fg-subtle', inMenu ? 'ml-auto' : 'hidden md:block')}
          />
        </button>
      }
    >
      <MenuList>
        {session.companies.map((item) => (
          <MenuListItem
            key={item.id}
            active={item.id === company.id}
            disabled={pending !== null}
            onClick={() => (item.id === company.id ? setOpen(false) : void switchTo(item.id))}
            trailing={
              item.id === company.id ? (
                <Icon name="check" className="text-brand" />
              ) : pending === item.id ? (
                <Icon name="loader-circle" className="animate-spin" />
              ) : null
            }
          >
            <span className="flex items-center gap-2.5">
              <CompanyMark company={item} />
              <span className="truncate">{item.name}</span>
            </span>
          </MenuListItem>
        ))}
      </MenuList>
    </ResponsivePopover>
  );
}
